import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";

export interface PermitSubNavigationItem {
  /** Label displayed to the user */
  label: string;
  /** Path to navigate to when the tab is clicked */
  path: string;
  /** Optional number to display as a badge */
  badge?: number;
}

export interface PermitSubNavigationProps {
  items: PermitSubNavigationItem[];
}

export const PermitSubNavigation: React.FC<PermitSubNavigationProps> = ({ items }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getActiveTabIndex = (): number => {
    const currentPath = location.pathname;

    // Find the matching tab index based on pathname
    const exactMatchIndex = items.findIndex((item) => item.path === currentPath);
    if (exactMatchIndex !== -1) {
      return exactMatchIndex;
    }

    // Check for nested route match (prefix match, but not for root /permits path)
    const nestedMatchIndex = items.findIndex(
      (item) => item.path !== "/permits" && currentPath.startsWith(item.path + "/"),
    );
    if (nestedMatchIndex !== -1) {
      return nestedMatchIndex;
    }

    // Default to first tab
    return 0;
  };

  const activeTab = getActiveTabIndex();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const targetItem = items[newValue];
    if (targetItem) {
      navigate(targetItem.path);
    }
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label="permit sub navigation">
        {items.map((item, index) => (
          <Tab
            key={item.path}
            label={
              item.badge !== undefined ? (
                <Badge badgeContent={item.badge} color="primary" max={999}>
                  <Box component="span" sx={{ pr: item.badge > 0 ? 2 : 0 }}>
                    {item.label}
                  </Box>
                </Badge>
              ) : (
                item.label
              )
            }
            id={`permit-subnav-tab-${index}`}
            aria-controls={`permit-subnav-tabpanel-${index}`}
            sx={{ textTransform: "none", fontWeight: 500 }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default PermitSubNavigation;
